import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { getAlertRules, setAlertRuleScoreWeight } from '../utils/api';
import { ROLE_SUPERADMIN } from '../utils/roles';

// The sensors in the order the page lists them, as the monthly report does.
const SENSORS = [
        { type: 'moist', label: 'Moisture' },
        { type: 'light', label: 'Light' },
        { type: 'temp', label: 'Temperature' },
        { type: 'nutrition', label: 'Nutrition' },
];

const parseWeight = (value) => {
        if (value === '' || !/^\d+$/.test(value)) return null;
        const n = Number(value);
        return n <= 100 ? n : null;
};

// ScoringPage lets superadmins set how many points each alert takes off a
// plant's score. A plant scores 100 minus, for every alert, the rule's weight
// times the share of the period it was open; rooms and sites average their
// plants. The dashboard, daily email and monthly report all use these weights.
const ScoringPage = () => {
        const { user } = useContext(AuthContext);
        const { showToast } = useToast();
        const [rules, setRules] = useState([]);
        const [drafts, setDrafts] = useState({}); // rule id -> input text
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);
        const [saving, setSaving] = useState(false);

        useEffect(() => {
                (async () => {
                        try {
                                const res = await getAlertRules();
                                if (!res.ok) throw new Error('Failed to load alert rules');
                                const { data } = await res.json();
                                setRules(data || []);
                        } catch (e) {
                                setError(e.message);
                        } finally {
                                setLoading(false);
                        }
                })();
        }, []);

        const valueOf = (rule) => (rule.id in drafts ? drafts[rule.id] : String(rule.score_weight));
        const changed = rules.filter(r => r.id in drafts && drafts[r.id] !== String(r.score_weight));
        const invalid = changed.some(r => parseWeight(drafts[r.id]) === null);

        // The lowest score a plant can get: every sensor in its heaviest alert
        // for the whole period.
        const worstCase = useMemo(() => {
                const heaviest = {};
                for (const r of rules) {
                        const w = parseWeight(valueOf(r)) ?? 0;
                        heaviest[r.sensor_type] = Math.max(heaviest[r.sensor_type] ?? 0, w);
                }
                return Math.max(0, 100 - Object.values(heaviest).reduce((a, b) => a + b, 0));
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [rules, drafts]);

        const save = async () => {
                setSaving(true);
                const saved = [];
                try {
                        for (const rule of changed) {
                                const res = await setAlertRuleScoreWeight(rule.id, parseWeight(drafts[rule.id]));
                                if (!res.ok) throw new Error(`status ${res.status}`);
                                const { data } = await res.json();
                                saved.push(data);
                        }
                        showToast('success', 'Score weights saved.');
                } catch (e) {
                        console.error('Failed to save score weights', e);
                        showToast('error', 'Could not save all score weights.');
                } finally {
                        const byId = Object.fromEntries(saved.map(r => [r.id, r]));
                        setRules(rs => rs.map(r => byId[r.id] || r));
                        setDrafts(d => {
                                const next = { ...d };
                                saved.forEach(r => delete next[r.id]);
                                return next;
                        });
                        setSaving(false);
                }
        };

        if (user?.roleId !== ROLE_SUPERADMIN) {
                return <Navigate to="/" replace />;
        }
        if (loading) {
                return <div className="page-gradient min-h-screen flex items-center justify-center">Loading...</div>;
        }
        if (error) {
                return <div className="page-gradient min-h-screen flex items-center justify-center text-status-critical">{error}</div>;
        }

        return (
                <div className="page-gradient min-h-screen">
                        <Header />
                        <main className="max-w-3xl mx-auto px-4 sm:px-8 py-8">
                                <section className="mb-8">
                                        <h1 className="text-3xl font-bold text-text-strong mb-2">Plant Score</h1>
                                        <p className="text-text-weak">
                                                A plant starts at 100. Each alert takes off its points times the share of the period it was open:
                                                an alert worth 40 that is open for half the week takes off 20. Rooms and sites average their plants.
                                                The dashboard scores the last 7 days, the monthly report each month.
                                        </p>
                                </section>

                                <div className="bg-white rounded-2xl card-shadow p-6 sm:p-8 space-y-6">
                                        {SENSORS.map(({ type, label }) => {
                                                const sensorRules = rules.filter(r => r.sensor_type === type);
                                                if (sensorRules.length === 0) return null;
                                                return (
                                                        <div key={type}>
                                                                <h2 className="font-semibold text-text-strong mb-2">{label}</h2>
                                                                <div className="divide-y divide-gray-100">
                                                                        {sensorRules.map(rule => {
                                                                                const value = valueOf(rule);
                                                                                const bad = parseWeight(value) === null;
                                                                                return (
                                                                                        <label key={rule.id} data-testid="score-weight-row" data-rule-id={rule.id} className="flex items-center justify-between gap-4 py-2">
                                                                                                <span className="text-text-weak">{rule.action_title}</span>
                                                                                                <span className="flex items-center gap-2">
                                                                                                        <input
                                                                                                                type="number"
                                                                                                                min="0"
                                                                                                                max="100"
                                                                                                                step="1"
                                                                                                                value={value}
                                                                                                                onChange={(e) => setDrafts(d => ({ ...d, [rule.id]: e.target.value }))}
                                                                                                                data-testid="score-weight-input"
                                                                                                                aria-invalid={bad}
                                                                                                                className={`w-20 px-3 py-1.5 border rounded-lg text-right ${bad ? 'border-status-critical' : 'border-gray-300'}`}
                                                                                                        />
                                                                                                        <span className="text-sm text-text-weak w-12">points</span>
                                                                                                </span>
                                                                                        </label>
                                                                                );
                                                                        })}
                                                                </div>
                                                        </div>
                                                );
                                        })}

                                        <div className="flex flex-wrap items-center justify-between gap-4 border-t pt-4">
                                                <p data-testid="score-worst-case" className="text-sm text-text-weak">
                                                        With every sensor in its heaviest alert all period, a plant scores <span className="font-semibold text-text-strong">{worstCase}</span>.
                                                </p>
                                                <button
                                                        type="button"
                                                        onClick={save}
                                                        disabled={saving || changed.length === 0 || invalid}
                                                        data-testid="score-weights-save"
                                                        className="px-5 py-2 rounded-lg bg-brand text-white font-medium disabled:opacity-50"
                                                >
                                                        {saving ? 'Saving…' : 'Save'}
                                                </button>
                                        </div>
                                        {invalid && <p className="text-sm text-status-critical">Points must be whole numbers from 0 to 100.</p>}
                                </div>
                        </main>
                        <Footer />
                </div>
        );
};

export default ScoringPage;
